import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { MenuPermissionGuard } from 'app/core/auth/guards/menu-permission.guard';
import { OnboardingGuard } from 'app/core/auth/guards/onboarding.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { administrationChildren } from 'app/routing/administration.routes';
import { authenticatedFeatureChildren } from 'app/routing/authenticated-feature-children.routes';
// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
  // Redirect empty path to sign-in
  { path: '', pathMatch: 'full', redirectTo: 'auth/sign-in' },

  // Redirect reset-password to auth/reset-password (preserve ?token=...)
  {
    path: 'reset-password',
    redirectTo: 'auth/reset-password',
    pathMatch: 'full',
  },

  // Redirect signed-in user based on their role
  //
  // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
  // path. This component will determine the appropriate redirect based on user role.
  {
    path: 'signed-in-redirect',
    loadChildren: () =>
      import('./modules/auth/signed-in-redirect/signed-in-redirect.routes').then(
        (m) => m.signedInRedirectRoutes,
      ),
  },

  // Convenience redirects for auth pages
  { path: 'sign-in', pathMatch: 'full', redirectTo: 'auth/sign-in' },
  { path: 'sign-up', pathMatch: 'full', redirectTo: 'auth/sign-up' },
  { path: 'forgot-password', pathMatch: 'full', redirectTo: 'auth/forgot-password' },

  // Auth routes for guests
  {
    path: 'auth',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'confirmation-required',
        loadChildren: () =>
          import('app/modules/auth/confirmation-required/confirmation-required.routes'),
      },
      {
        path: 'forgot-password',
        loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes'),
      },
      {
        path: 'reset-password',
        loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes'),
      },
      {
        path: 'otp-verification',
        loadChildren: () => import('app/modules/auth/otp-verification/otp-verification.routes'),
      },
      {
        path: 'direct-login',
        loadChildren: () => import('app/modules/auth/direct-login/direct-login.routes'),
      },
      {
        path: '',
        loadChildren: () => import('app/modules/auth/auth-fade/auth-fade.routes'),
      },
    ],
  },

  // Auth routes for authenticated users
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'sign-out',
        loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes'),
      },
      {
        path: 'unlock-session',
        loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes'),
      },
      {
        path: 'unauthorized',
        loadComponent: () =>
          import('app/modules/error/unauthorized/unauthorized.component').then(
            (m) => m.UnauthorizedComponent,
          ),
      },
    ],
  },

  // Landing routes
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'home',
        loadChildren: () => import('app/modules/landing/home/home.routes'),
      },
      {
        path: 'pricing',
        loadChildren: () =>
          import('app/modules/pricing/pricing.routes').then((m) => m.pricingRoutes),
      },
      {
        path: 'payment',
        loadChildren: () =>
          import('app/modules/payment/payment.routes').then((m) => m.paymentRoutes),
      },
    ],
  },

  // Administration (access control, admin, onboarding, invoice numbering)
  {
    path: '',
    canActivate: [AuthGuard, OnboardingGuard, MenuPermissionGuard],
    canActivateChild: [AuthGuard, OnboardingGuard, MenuPermissionGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    children: administrationChildren,
  },

  // Masters, transactions, reports, utilities, dashboards & integrations (same URL paths as before)
  {
    path: '',
    canActivate: [AuthGuard, OnboardingGuard, MenuPermissionGuard],
    canActivateChild: [AuthGuard, OnboardingGuard, MenuPermissionGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    children: authenticatedFeatureChildren,
  },
];
